// Busca e marcação automática de faturas no acordo de parcelamento.
//
// Vive separado do modal para ter teste: é a regra que decide de QUEM é a
// dívida que entra no termo. Errar aqui põe a fatura de outra pessoa dentro de
// uma confissão de dívida.
//
// O defeito que originou este arquivo (23/09/2026): a tela marcava o pagador
// que vinha primeiro em ORDEM ALFABÉTICA, não o que o operador digitou. Com a
// segunda passada por CPF trazendo gente de outro nome, digitar o nome exato
// de um pagador marcava outra pessoa em 42 de 571 buscas ("GABRIELA ..." marcava
// "BRUNO ..."). E cada busca intermediária enquanto se digitava deixava marcas
// que nunca saíam.

import { docPagador, mesmoPagador, nomePagador, type Pagador } from './parcelamentoRegras.ts';

export interface FaturaBusca extends Pagador {
  id: string | number;
  uc_codigo?: string | null;
}

/** Sem acento, maiúsculo, só letras/números e espaço simples. */
export function normalizarTexto(v: unknown): string {
  return String(v ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const soDigitos = (v: unknown) => String(v ?? '').replace(/\D/g, '');
const ucNorm = (v: unknown) => soDigitos(v).replace(/^0+/, '');

/**
 * Quanto a fatura casa com o que foi digitado — sempre pelo lado de QUEM PAGA.
 *
 * Casar só pelo titular (o dono da conta de luz, quando o boleto sai no nome
 * de outro) vale zero de propósito: a fatura aparece na lista, mas a tela não
 * marca sozinha a dívida de alguém cujo nome o operador não escreveu.
 */
export function pontuarPagador(termo: string, f: FaturaBusca): number {
  const digitos = soDigitos(termo);
  if ((digitos.length === 11 || digitos.length === 14) && docPagador(f) === digitos) return 100;

  const t = normalizarTexto(termo);
  const nome = normalizarTexto(nomePagador(f));
  if (t && nome) {
    if (nome === t) return 90;
    if (nome.startsWith(t)) return 70;
    if (nome.includes(t)) return 60;
    // "maria souza" acha "MARIA APARECIDA SOUZA": todas as palavras, fora de ordem.
    const palavras = t.split(' ').filter((p) => p.length >= 2);
    const doNome = new Set(nome.split(' '));
    if (palavras.length > 1 && palavras.every((p) => doNome.has(p))) return 50;
  }

  // Número de UC: 11 dígitos também é tamanho de CPF, por isso vem depois do
  // documento e compara a UC normalizada inteira, não um pedaço.
  if (digitos.length >= 5) {
    const uc = ucNorm(f.uc_codigo);
    if (uc && uc === digitos.replace(/^0+/, '')) return 80;
  }
  return 0;
}

/** Faturas agrupadas por pagador, na ordem em que cada pagador aparece.
 *  Usa a MESMA regra que marca (`mesmoPagador`) — contar grupos por outra
 *  chave dizia "2 pagadores" para uma pessoa só. */
export function agruparPorPagador<T extends Pagador>(faturas: T[]): T[][] {
  const grupos: T[][] = [];
  for (const f of faturas) {
    const g = grupos.find((gr) => mesmoPagador(f, gr[0]));
    if (g) g.push(f); else grupos.push([f]);
  }
  return grupos;
}

/**
 * Qual pagador a tela marca sozinha. `null` = nenhum.
 *
 * Só marca quando UM pagador é claramente o que foi digitado. Empate (buscar
 * "MARIA" e aparecerem três Marias) não marca ninguém: adivinhar ali é pôr a
 * dívida de um estranho no termo, e a lista agrupada deixa o operador escolher
 * com um clique.
 */
export function escolherPagadorDaBusca<T extends FaturaBusca>(termo: string, faturas: T[]): T | null {
  let melhor = 0;
  let empatados: T[] = [];
  for (const grupo of agruparPorPagador(faturas)) {
    const nota = Math.max(...grupo.map((f) => pontuarPagador(termo, f)));
    if (nota > melhor) { melhor = nota; empatados = [grupo[0]]; }
    else if (nota === melhor && nota > 0) empatados.push(grupo[0]);
  }
  return melhor > 0 && empatados.length === 1 ? empatados[0] : null;
}

/**
 * Da segunda passada (por documento), fica só a fatura PAGA por um daqueles
 * documentos. A consulta procura o documento nas duas colunas (titular e
 * pagador); a linha em que ele é só o titular é dívida de outra pessoa — era
 * ela que trazia nomes que o operador não digitou.
 */
export function filtrarPeloPagador<T extends Pagador>(faturas: T[], documentos: string[]): T[] {
  const alvo = new Set(documentos.map(soDigitos).filter(Boolean));
  return faturas.filter((f) => alvo.has(docPagador(f)));
}

/** Ordem da lista: o pagador mais parecido com o que foi digitado primeiro; dentro
 *  dele, do vencimento mais antigo ao mais novo (é assim que a dívida é lida em
 *  voz alta com o cliente na linha). */
export function ordenarResultado<T extends FaturaBusca & { vencimento_boleto?: string | null }>(
  termo: string,
  faturas: T[],
): T[] {
  const grupos = agruparPorPagador(faturas).map((g) => ({
    g,
    nota: Math.max(...g.map((f) => pontuarPagador(termo, f))),
    nome: nomePagador(g[0]),
  }));
  grupos.sort((a, b) => b.nota - a.nota || a.nome.localeCompare(b.nome));
  return grupos.flatMap(({ g }) =>
    [...g].sort((a, b) => String(a.vencimento_boleto || '').localeCompare(String(b.vencimento_boleto || ''))));
}

/** O que a busca do acordo aceita — a consulta do passo 1 do modal filtra por
 *  estas listas, e `motivoForaDoAcordo` explica a mesma regra em palavras. */
export const STATUS_BOLETO_NEGOCIAVEL = ['Em aberto', 'Vencido'] as const;
export const STATUS_FATURA_NEGOCIAVEL = ['Com boleto calculado', 'Com boleto emitido / A enviar', 'Cobrança enviada'] as const;

export interface FaturaNegociavel {
  status_boleto_vigor?: string | null;
  status_fatura?: string | null;
  valor_boleto_vigor?: number | string | null;
  parcelamento_origem_id?: string | null;
  data_pgto_boleto?: string | null;
  quitado_em?: string | null;
}

/**
 * Por que a fatura não pode entrar num acordo — `null` quando pode.
 *
 * É o filtro da busca do passo 1, dito em palavras. A fatura que chega por
 * atalho (a rota de cobrança, o link do WhatsApp) não passa por aquela busca: sem
 * esta checagem entrava no acordo o que a busca nunca deixaria — parcela de outro
 * acordo, boleto já pago, fatura de boleto único com valor da Vigor zerado.
 *
 * A data de pagamento vale como prova mesmo com o status desatualizado (regra
 * "campo OU data" de statusBoleto.ts): pôr fatura paga no acordo é cobrar duas
 * vezes.
 */
export function motivoForaDoAcordo(f: FaturaNegociavel): string | null {
  if (f.parcelamento_origem_id) return 'já é cobrança de um acordo (entrada ou parcela)';
  const status = String(f.status_boleto_vigor ?? '').trim();
  if (status === 'Pago' || status === 'Quitado' || f.data_pgto_boleto || f.quitado_em) return 'já foi paga';
  if (status === 'Parcelado') return 'já está parcelada';
  if (status.toLowerCase().startsWith('cancelad')) return 'boleto cancelado';
  if (!(STATUS_BOLETO_NEGOCIAVEL as readonly string[]).includes(status)) return `boleto "${status || 'sem status'}"`;
  if (!(STATUS_FATURA_NEGOCIAVEL as readonly string[]).includes(String(f.status_fatura ?? ''))) {
    return 'ainda sem boleto calculado';
  }
  if (!(Number(f.valor_boleto_vigor) > 0)) return 'sem valor da Vigor no boleto (boleto único ou de parceira)';
  return null;
}

export interface PlanoMarcacao {
  /** Ids que a tela passa a marcar sozinha (substituem as automáticas anteriores). */
  marcar: string[];
  /** Automáticas da busca anterior que saem — o operador nunca as confirmou. */
  desmarcar: string[];
  /** Fatura que representa o pagador escolhido, ou null se ninguém foi marcado. */
  referencia: FaturaBusca | null;
}

/**
 * O que marcar quando chega um resultado novo de busca.
 *
 * - Marcação automática NÃO se acumula entre buscas: o que a tela marcou para
 *   "Gab" sai quando o resultado de "Gabriela Martins" chega. Era assim que
 *   nomes que ninguém escreveu iam parar no acordo.
 * - O que o operador marcou na mão (ou veio semeado pelo atalho da fatura) é
 *   dele: nunca é desmarcado aqui. Se alguma dessas faturas está no resultado,
 *   o pagador dela é a referência; se nenhuma está, ele está montando a lista
 *   na mão e a tela não mistura outro pagador.
 */
export function planejarMarcacao(
  termo: string,
  resultado: FaturaBusca[],
  selecionadas: Iterable<string>,
  automaticas: Iterable<string>,
): PlanoMarcacao {
  const auto = new Set(Array.from(automaticas, String));
  const manuais = new Set(Array.from(selecionadas, String).filter((id) => !auto.has(id)));

  let referencia: FaturaBusca | null = null;
  if (manuais.size > 0) {
    referencia = resultado.find((f) => manuais.has(String(f.id))) ?? null;
  } else {
    referencia = escolherPagadorDaBusca(termo, resultado);
  }

  const marcar = referencia
    ? resultado.filter((f) => mesmoPagador(f, referencia!) && !manuais.has(String(f.id))).map((f) => String(f.id))
    : [];
  const ficam = new Set(marcar);
  const desmarcar = [...auto].filter((id) => !ficam.has(id));
  return { marcar, desmarcar, referencia };
}
